import React from 'react';
import { noop } from 'lodash';
import Button, { Type } from 'src/components/button';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';
import './style.scss';

const { TextArea } = Input;

/**
 * CommentEditor component - to be used inside CommentModal.
 * 
 * @param {Object} props - Props for the CommentEditor component.
 * @param {string} props.value - An empty object or an object with attributes if comment is being edited.
 * @param {function} props.onSend - Function to be called when sending the comment.
 * @param {Boolean} props.loading - Indicates if it is loading.
 * @param {setState} props.onChange - Change current comment.
 * @param {Boolean} props.isEditing - Indicates whether it is editing or creating.
 * @param {function} props.stopEditing - Function to be called when stopping the editing.
 * @param {Boolean} props.hasFit - Indicates if deallocation has fit.
 * @returns {JSX.Element} The CommentEditor component.
 * @example
 * 	<CommentEditor
		value={
				id: '',
				message: ''
		}
		onSend={sendFunction}
		loading={false}
		onChange={setComment}
		isEditing={false}
		stopEditing={stopEditingFunction}
		hasFit={true}
	/>
 */
function CommentEditor({
	value,
	onSend,
	loading,
	onChange,
	isEditing,
	stopEditing,
}) {
	const { t } = useTranslation();

	return (
		<div className='comment-container'>
			<div className='comment-editor'>
				<div className='field-container'>
					<TextArea
						value={value.message}
						onChange={(e) =>
							onChange({ id: value.id, message: e.target.value })
						}
						disabled={loading}
						maxLength={400}
						aria-placeholder={t('commentAriaPlaceholder')}
						role='textbox'
					/>
				</div>
				<div className='button-container'>
					<Button
						type={Type.Primary}
						className='send-button'
						disabled={!value.message || loading}
						onClick={() => onSend()}
					>
						{isEditing ? t('edit') : t('add')}
					</Button>
					{isEditing && (
						<Button
							type={Type.Secondary}
							className='cancel-button cit-btn link-type'
							onClick={() => stopEditing()}
						>
							{t('cancel')}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}

CommentEditor.defaultProps = {
	value: {},
	onSend: noop,
	loading: false,
	onChange: noop,
	isEditing: false,
	stopEditing: noop,
};

export default CommentEditor;
